import React, { createContext, useContext, useEffect, useLayoutEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

const ThemeContext = createContext();

const getDirectionForLanguage = (language) => (
  language?.startsWith('ar') ? 'rtl' : 'ltr'
);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const { i18n } = useTranslation();
  const [theme, setTheme] = useState('light');
  const [direction, setDirection] = useState(() => getDirectionForLanguage(i18n.resolvedLanguage || i18n.language));

  useLayoutEffect(() => {
    // Set direction based on language
    const currentLanguage = i18n.resolvedLanguage || i18n.language;
    const newDirection = getDirectionForLanguage(currentLanguage);
    const isArabic = newDirection === 'rtl';
    setDirection(newDirection);

    // Update document attributes
    document.documentElement.setAttribute('dir', newDirection);
    document.documentElement.setAttribute('lang', currentLanguage);
    document.body.setAttribute('dir', newDirection);
    document.body.setAttribute('lang', currentLanguage);
    document.body.dataset.direction = newDirection;

    // Update body class for font family
    if (isArabic) {
      document.body.classList.add('font-arabic');
      document.body.classList.remove('font-english');
    } else {
      document.body.classList.add('font-english');
      document.body.classList.remove('font-arabic');
    }
  }, [i18n.language, i18n.resolvedLanguage]);

  useEffect(() => {
    // Load theme from localStorage
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) {
      setTheme(savedTheme);
    }
  }, []);

  useEffect(() => {
    // Apply theme to document
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    
    // Save theme to localStorage
    localStorage.setItem('theme', theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prev => prev === 'light' ? 'dark' : 'light');
  };

  const value = {
    theme,
    direction,
    toggleTheme,
    isRTL: direction === 'rtl',
    isDark: theme === 'dark',
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};
